<template>
  <div class="container mt-1 p-0">
    <b-alert v-model="showNewAssignAlert" variant="success" dismissible>
      <strong>A new assigned location was automatically created for you!</strong>
      <span class="d-block mt-2">
      Want to make more observations at this location for repeat monitoring? Instead of recreating it, please note:
    </span>
      <ul class="mb-0">
        <li>You can save time by selecting this location on datasheets that offer location selection
        </li>
        <li>You may view your
          <b-link v-if="observation && observation.project"
                  :to="{name: 'ProjectAssignedLocations', params:{id:observation.project.urlField}}"><u>assigned
            locations</u>
          </b-link>
          for this project at anytime.
        </li>
      </ul>
    </b-alert>
    <b-alert v-model="showSciStarterAlert" variant="success" dismissible>
      <strong>Success! Your observation has been saved. You can track this and
        other contributions on your SciStarter dashboard.</strong>
      <i v-b-tooltip.hover title="The manager(s) of this project have enabled SciStarter integration.
          If you have an account (with the same email address) on SciStarter,
          you will be able to track your participation on your SciStarter
          dashboard." class="fa fa-question-circle ml-2"></i>
    </b-alert>
    <div class="observation-details-header flex-shrink-1 d-flex flex-wrap bg-white px-3 py-2">
      <h2 class="ml-0 mt-0 mb-0">Observation Details</h2>
      <div v-if="!isModal" class="text-right float-right">
        <div class="btn-group">
          <div class="mr-2 mt-2">
            <router-link
              v-if="observation && observation.project"
              :to="{name:'ProjectShow', params:{id:observation.project.urlField}}"
            >
              <i class="fa fa-arrow-right mr-1"></i>To Project
            </router-link>
          </div>
          <div class="ml-2">
            <b-button
              class="btn btn-primary"
              id="CreateNewObservationButton"
              title="Add a new observation"
              alt="Add a new observation"
              v-if="observation && observation.project && isProjectRoleMember(observation.project.id)"
              variant=""
              @click="clickAddDataLink"
              style="max-width:150px;"
            >
              <i class="fa fa-plus mr-1"/>
              <span class="label-text HideResponsive">
              New
            </span>
            </b-button>
          </div>

          <div class="ml-1">
            <router-link
              class="btn btn-warning"
              v-if="canEdit"
              alt="Edit this observation"
              title="Edit this observation"
              :to="{ name: 'ObservationEdit', params: { id: observation.id} }"
            >
              <i class="fa fa-pencil" style="color:white;"></i>
              <span class="ml-1 label-text HideResponsive">Edit</span>
            </router-link>
          </div>

          <div class="ml-1 mr-3">
            <b-button
              v-if="canDelete"
              variant="danger"
              @click="onDeleteObservationClick($event)"
              alt="Delete this observation"
              title="Delete this observation"
            >
              <i class="fa fa-trash "></i>
              <span class="ml-1 label-text HideResponsive">Delete</span>
            </b-button>
          </div>
        </div>
      </div>
    </div>
    <b-container>
      <b-alert
        :show="error && error.length > 0"
        variant="danger"
      >
        {{ error }}
      </b-alert>
      <div class="my-0">
        <div>
          <div class="row">
            <div class="col-12 col-md-7 mb-4" :style="{minHeight: '300px'}">
              <ObservationViewLocation
                v-if="observation && observation.lngLat"
                :lngLat="observation.lngLat"
              />

            </div>
            <div class="col-12 col-md-5 mb-4">
              <div class="row">
                <div class="col-lg-12 col-12 pt-3" style="z-index: 1;">
                  <b-carousel
                    v-if="observation && observation.project"
                    id="carousel-1"
                    class="my-custom-carousel"
                    v-model="slide"
                    :interval="0"
                    controls
                    indicators
                    background="#ababab"
                    img-width="400"
                    img-height="300"
                    style="text-shadow: 1px 1px 2px #333; border: solid 1px grey; height:300px; margin-top:-14px; z-index: 1; overflow:hidden;"
                    @sliding-start="onSlideStart"
                    @sliding-end="onSlideEnd"
                  >
                    <b-carousel-slide
                      :class="'' + (img.current ? ' selected' : '')"
                      v-for="(img, index) in images"
                      :key="img.path + '-' + index"
                      :img-src="img.path"
                    ></b-carousel-slide>
                  </b-carousel>
                  <!--                  <font-awesome-icon :icon="['far', 'star']" />-->
                </div>
                <div style="z-index: 999; margin-top:-44px; margin-left:auto; margin-right:23px;">
                  <b-button
                    v-if="canEdit"
                    class="btn btn-primary mt-1"
                    @click="$refs.addPhotoChoose.click()"
                    alt="Add photos to this observation"
                    title="Add photos to this observation"
                  >
                    <input
                      v-if="canEdit"
                      class="d-none"
                      @change="onFileUploadChange"
                      accept="image/*"
                      type="file"
                      ref="addPhotoChoose"
                    />
                    <span class="btn-font-awesome mr-2">
                    <font-awesome-icon icon="plus-circle"/>
                    <i class="fa fa-1x fa-spin fa-spinner ml-1" v-if="isLoading || isLoadingLocationUpdate"></i>
                      <!--<VueLoader v-if="isLoading || isLoadingLocationUpdate" style="max-height:18px;" class="d-inline"/>-->
                  </span
                  ><span class="d-none d-md-inline">Add Photos</span>
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <ObservationViewMetadata :observation="observation"></ObservationViewMetadata>

          <b-row v-if="(observation && observation.comments)" class="row my-2">
            <b-col class="col-lg-2 col-md-3 mb-2 font-weight-bold text-muted">Comments:</b-col>
            <b-col class="col-lg-6 col-md-6 mb-2 font-weight-bold">
              {{ observation.comments }}
            </b-col>
          </b-row>

          <p class="p-down pt-3" style="line-height:0.5;" v-if="false">
            <b-row class="row mb-2" v-if="observation && observation.project">
              <b-col class="d-none d-md-table-cell col-md-4 col-lg-3 mb-2 ">
                Project:
              </b-col>
              <b-col class="col-md-8 col-lg-9 mb-2 ">
              <span class="">
                <router-link
                  :to="{name:'ProjectShow', params:{id:observation.project.urlField}}"
                  class="clearBoth font-weight-normal "
                >
                  {{ observation.project.name }}
                </router-link>
              </span>
              </b-col>
            </b-row>
            <b-row class="row mb-2" v-if="observation && observation.datasheet && observation.datasheet.name">
              <b-col class="d-none d-md-table-cell col-md-4 col-lg-3 mb-2 ">
                Datasheet:
              </b-col>
              <b-col class="col-md-8 col-lg-9 mb-2 ">
              <span class="">
                {{ observation.datasheet.name }}
              </span>
              </b-col>
            </b-row>

            <b-row class="row mb-2" v-if="observation && observation.project">
              <b-col class="d-none d-md-table-cell col-md-4 col-lg-3 mb-2">
                Location:
              </b-col>
              <b-col
                class="col-md-8 col-lg-9 mb-2"
                v-if="!editField.isLocationEdit"
              >
                <router-link
                  :to="{name:'LocationDetails', params:{id: observation.location.id}}"
                  v-if="observation && observation.location && observation.location.name"
                  class="clearBoth font-weight-normal "
                >
                  {{ observation.location.name }}
                </router-link>
                <span
                  class="ml-1 d-none d-md-inline"
                  v-if="observation && observation.lngLat"
                >
                ({{ observation.lngLat.lat }}, {{ observation.lngLat.lng }})
              </span>
              </b-col>
            </b-row>

            <b-row class="row mb-2" v-if="observation && observation.project">
              <div class="d-none d-md-table-cell col-md-4 col-lg-3 mb-2">
                Observed By:
              </div>
              <div class="col-md-8 col-lg-9 mb-2">
                {{ observation.user.displayName }}<span v-if="isObserver(observation)"> (You)</span>
              </div>
            </b-row>

            <ObservationDateTime
              :dateTime="observation.observedAt"
              :canEdit="false"
              :observationId="observation.id"
              :date-format="'MMM D, YYYY, h:mm:ss A'"
              v-if="observation && observation.observedAt"
            >
            </ObservationDateTime>

            <b-row class="row mb-2" v-if="observation">
              <div class="d-none d-md-table-cell col-md-4 col-lg-3 mb-2">
                Created At:
              </div>
              <div class="col-md-8 col-lg-9 mb-2">
                <DateTime v-if="observation.createdAt" :date="observation.createdAt" dateTime></DateTime>
              </div>
            </b-row>

            <!--              <b-row class="row mb-2" v-if="observation">-->
            <!--                <div class="d-none d-md-table-cell col-md-4 col-lg-3 mb-2">-->
            <!--                  Updated At:-->
            <!--                </div>-->
            <!--                <div class="col-md-8 col-lg-9 mb-2">-->
            <!--                  <DateTime v-if="observation.updatedAt" :date="observation.updatedAt" dateTime></DateTime>-->
            <!--                </div>-->
            <!--              </b-row>-->


            <b-row v-if="(observation && observation.comments)" class="row mb-2">
              <b-col class="col-md-4 col-lg-3 mb-2">Comments:</b-col>
              <b-col class="col-md-8 col-lg-9 mb-2 limited-description">
                <span class="limited-description">{{ observation.comments }}</span>
              </b-col>
            </b-row>
          </p>

          <div class="mt-2" v-if="observation && observation.project">
            <ObservationRecordList :records="observation.records"/>
          </div>
        </div>
      </div>

      <div v-if="images && images.length > 0">
        <h2>Observation Image Gallery</h2>
        <p class="p-down">
          Below is a gallery of all photos submitted for this observation,
          including images in response to specific questions and images appended
          to the observation that apply broadly to the observation as a whole. The
          carousel at top right also shows all of these same observation images in
          carousel fashion.
        </p>
        <b-card-group columns class="mt-4">
          <b-card
            v-for="(img, index) in images"
            :img-src="img.path"
            :key="img.path + '-' + index"
            img-alt="Image"
            img-top
          ><!-- :key="index"   title="Card title that wraps to a new line" -->
            <!--<b-card-text>
              This is a wider card with supporting text below as a natural lead-in to additional content.
              This content is a little bit longer.
            </b-card-text>-->
          </b-card>
        </b-card-group>
      </div>
    </b-container>
    <VueLoader v-if="isLoading || isLoadingLocationUpdate"/>
    <div class="mt-0">
      <b-modal
        ref="confirmationModal"
        id="confirmation-modal"
        title="Confirmation"
        hide-footer
      >
        <p class="my-4">
          Are you sure you want to permanently delete this observation? This action cannot be undone.
        </p>
        <div class="float-right">
          <b-button class="btn btn-blue" @click="onYesClick">Yes</b-button>
          <b-button @click="onNoClick">No</b-button>
        </div>
      </b-modal>
    </div>
  </div>

</template>

<script>

import VueLoader from "../../../components/layout/Loader";
import DatePicker from "vue-bootstrap-datetimepicker";
import moment from "moment";
import "pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css";
import {mapActions, mapGetters} from "vuex";
import {BCarousel} from "bootstrap-vue";
import ObservationDateTime from '../../../components/observation/ObservationDateTime.vue'
import ObservationRecordList from "../../../components/observation/ObservationRecordList"
import DateTime from "../../../components/common/date-time/DateTime.vue";
import ObservationViewLocation from "./observationViewLocation.vue";
import ObservationViewMetadata from "./observationViewMetadata.vue";
import FILE_ACTION_TYPES from "../../../store/constants/actionTypes";

export default {
  components: {
    VueLoader,
    DatePicker,
    BCarousel,
    ObservationDateTime,
    ObservationRecordList,
    DateTime,
    ObservationViewLocation,
    ObservationViewMetadata,
  },

  provide() {
    return {parentValidator: null};
  },

  props: {
    observationId: {
      type: String,
      default: null,
    },
    isNewAutoAssign: {
      type: Boolean,
      default: false,
    },
    addedNewObservation: {
      type: Boolean,
      default: false,
    },
  },

  data() {

    let tableItems = [];
    let user = JSON.parse(localStorage.getItem("sessionData"));

    return {

      mapOptions: {
        streetViewControl: false,
        fullscreenControl: false,
        scaleControl: false,
        zoomControl: true,
        mapTypeControl: true
      },
      locationData: null,

      showNewAssignAlert: this.isNewAutoAssign,
      options: {
        format: "YYYY/MM/DD h:mm A",
        showClose: true
      },
      locationModel: {
        latitude: "",
        longitude: "",
        locationName: ""
      },
      editField: {
        isLocationEdit: false
      },
      showEditDate: false,
      slide: 0,
      sliding: null,
      deleteRecordId: 0,
      orderDeleted: -1,
      recordImage: null,
      previewNewRecordImage: null,
      previewEditRecordImage: null,
      recordIndex: -1,
      parentValidator: null,
      questions: [],
      currentUrl: window.location.href,
      selected: null,
      textInputVal: "",
      editDateValue: "",
      showAddModeData: false,
      addMoreDataQ1: 1,
      addMoreDataMN: "",
      addMoreDataValueType: "",
      addMoreDataValueTypeList: [
        {value: "", text: "--Select--"},
        {value: "number", text: "Numerical"},
        {value: "text", text: "Text (Single Line)"},
        {value: "textarea", text: "Text (Multiline)"},
        {value: "datetime", text: "Date and time"},
        {value: "radio", text: "Radio"},
        {value: "dropdown", text: "Dropdown"},
        {value: "image", text: "Image"}
      ],
      addMoreDataValue: "",
      addMoreDataUnits: "",
      textAreaValue: "",
      dateValue: "",
      showForm: true,
      loggedInUser: user,
      lat: 45,
      lng: 27,
      tableItems: [],
      edit: false,
      displayValue: false,
      displayUnits: false,
      displayText: false,
      displayDateTime: false,
      displayImage: false,
      displayRadio: false,
      displayDropdown: false,
      images: [],
      bigImageUrl: "/assets/images/generic_img.png",
      files: "",
      recordImageUpload: null,
      dropDownList: [
        {
          fieldVal: ""
        }
      ],
      chooseList: [
        {
          fieldVal: ""
        }
      ],
      editActive: false,
      isModal: false,
    };
  },

  computed: {
    ...mapGetters({
      isLoading: "observation/view/isLoading",
      observation: "observation/view/observation",
      error: "observation/view/error",
      isLoadingLocationUpdate: "location/isLoading",
      deletedSuccess: "observation/show/deletedSuccess",
      userData: 'user/profile/sessionData'
    }),
    canEdit() {
      if(!this.observation || !this.observation.project) {
        return false;
      }
      if(this.isProjectRoleManager(this.observation.project.id)) {
        return true;
      }
      if(this.isObserver(this.observation) && this.isProjectRoleMember(this.observation.project.id)) {
        return true;
      }
      return false;
    },
    canDelete() {
      if(!this.observation || !this.observation.project) {
        return false;
      }
      if(this.isProjectRoleManager(this.observation.project.id)) {
        return true;
      }
      if(this.isObserver(this.observation)) {
        return true;
      }
      return false;
    },
    observedAtFormatted() {
      if (this.observation && this.observation.observedAt) {
        return this.getDisplayDateTime(this.observation.observedAt);
      }
      return "";
    },
    showSciStarterAlert() {
      return false;
      // if (this.observation && this.observation.project) {
      //   let project = this.observation.project;
      //   if (this.addedNewObservation && project.isSciStarter && project.sciStarterID) {
      //     return true;
      //   }
      // }
      // return false;
    }
  },

  watch: {
    /**
     * @function observation - function that is called when this.observation is changed
     * @param newValue
     */
    observation(newValue) {

      if (!newValue) return

      // Collect all the photos
      this.images = [];

      // From observations records
      newValue.records.forEach(rec => {
        if (rec.recordType === 'image') {
          rec.files.forEach(img => {
            this.images.push({
              path: img.path,
              current: this.images.length === 0,
              isFeatured: newValue.featuredPhoto ? newValue.featuredPhoto.path === img.path : false,
            });
          })

        }
      })

      // From observation resources
      newValue.files.forEach((observation, index) => {
        this.images.push({
          path: observation.file.path,
          current: this.images.length === 0,
          isFeatured: newValue.featuredPhoto ? newValue.featuredPhoto.path === observation.file.path : false,
        });
      });
    },

    "observation.records"(oldVal, newVal) {
      // if (newVal) {
      //   // From photo observation records
      //   newVal.forEach(rec => {
      //     if (rec.recordType === 'image') {
      //       rec.files.forEach(img => {
      //         this.images.push({
      //           path: img.path,
      //           current: this.images.length === 0,
      //         });
      //       })
      //     }
      //   })
      // }
    },

    /**
     * @function addMoreDataQ1 - function that is called when this.addMoreDataQ1 is changed
     * @param value
     */
    addMoreDataQ1(value) {
      if (value === 2) {
        this.showForm = false;
      } else {
        this.showForm = true;
      }
    },

    /**
     * @function addMoreDataValueType - function that is called when this.addMoreDataValueType is changed
     * @param value
     */
    addMoreDataValueType(value) {
      this.previewNewRecordImage = null;

      if (value === "number") {
        this.displayValue = true;
        this.displayUnits = true;
        this.displayText = false;
        this.displayDateTime = false;
        this.addMoreDataValue = 0;
        this.displayImage = false;
        this.displayRadio = false;
        this.displayDropdown = false;
      } else if (value === "text" || value === "textarea") {
        this.displayText = true;
        this.displayValue = false;
        this.displayUnits = false;
        this.displayDateTime = false;
        this.displayImage = false;
        this.displayRadio = false;
        this.displayDropdown = false;
      } else if (value === "datetime") {
        this.displayText = false;
        this.displayValue = false;
        this.displayUnits = false;
        this.displayDateTime = true;
        this.displayImage = false;
        this.displayRadio = false;
        this.displayDropdown = false;
      } else if (value === "image") {
        this.displayText = false;
        this.displayValue = false;
        this.displayUnits = false;
        this.displayDateTime = false;
        this.displayImage = true;
        this.displayRadio = false;
        this.displayDropdown = false;
      } else if (value === "dropdown") {
        this.displayText = false;
        this.displayValue = false;
        this.displayUnits = false;
        this.displayDateTime = false;
        this.displayImage = false;
        this.displayRadio = false;
        this.displayDropdown = true;
      } else if (value === "radio") {
        this.displayText = false;
        this.displayValue = false;
        this.displayUnits = false;
        this.displayDateTime = false;
        this.displayImage = false;
        this.displayRadio = true;
        this.displayDropdown = false;
      } else {
        this.displayText = false;
        this.displayValue = false;
        this.displayUnits = false;
        this.displayDateTime = false;
        this.displayImage = false;
        this.displayRadio = false;
        this.displayDropdown = false;
      }
    },

    /**
     * @function images - function that is called when this.images is changed
     * @param val
     */
    images(val) {
      if (val.length > 0) {
        this.bigImageUrl = val[0].path;
      } else {
        this.bigImageUrl = "";
      }
    }
  },

  methods: {
    ...mapActions({
      // getObservation: "observation/show/getObservation",
      getObservation: "observation/view/get",
      updateObservation: "observation/view/update",
      deleteObservation: "observation/show/deleteObservation",
      resetError: "observation/show/resetError",
      setError: "observation/show/setError",
      uploadFile: "observation/show/uploadObservationImage",
      createRecord: "observation/show/createRecord",
      observationUpdate: "observation/show/observationUpdate",
      mainObservationDetailsUpdate:
        "observation/show/mainObservationDetailsUpdate",
    }),

    /**
     * @function onEditTopDetails
     * @param detailsName
     * @param currItem
     * Function gets called when the main observation details will be in the editing mode
     */
    onEditTopDetails(detailsName, currItem) {
      if (detailsName === "date") {
        this.editField.isObservedDateEdit = true;
        let d = new Date(this.getDisplayDateTime(currItem.observedAt));
        this.editDateValue = new Date(
          this.getDisplayDateTime(currItem.observedAt)
        );
      } else if (detailsName === "location") {
        this.editField.isLocationEdit = true;
        this.locationModel.locationName = currItem.location.name;
        this.locationModel.longitude = currItem.location.longitude;
        this.locationModel.latitude = currItem.location.latitude;
      }
    },

    /**
     * @function onCancelMainDetailsEditing
     * @param detailsName
     * Function gets called when we cancel the main observation details edit mode
     */
    onCancelMainDetailsEditing(detailsName) {
      if (detailsName === "date") {
        this.editField.isObservedDateEdit = false;
        this.editDateValue = "";
      } else if (detailsName === "location") {
        this.editField.isLocationEdit = false;
        this.locationModel = {
          locationName: "",
          latitude: "",
          longitude: ""
        };
      }
    },

    /**
     * @function onSlideStart
     * @param slide
     * Event Gets fired when slide for the carousel starts
     */
    onSlideStart(slide) {
      this.sliding = true;
    },

    /**
     * @function onSlideEnd
     * @param slide
     * Event Gets fired when slide for the carousel ends
     */
    onSlideEnd(slide) {
      this.sliding = false;
    },


    /**
     * @function onSubmitData
     * @param currItem
     * @param modalVal
     * @param detailsName
     */
    onSubmitData(currItem, modalVal, detailsName) {
      // it looks like this function is no longer in use. We do not allow partial edits to an observation.
      let observationMainData;
      if (detailsName === "date") {
        this.observation.observedAt = new Date(this.editDateValue);
      } else if (detailsName === "location") {
        this.observation.location.name = this.locationModel.locationName;

        const data = {
          id: this.observation.location.id,
          locationInfo: this.observation.location
        };
        this.updateLocationInfo(data).then(result => {
          debugger;
          this.observation.location = result;
          this.onCancelMainDetailsEditing(detailsName);
        });
      }
      if (detailsName !== "location") {
        this.mainObservationDetailsUpdate({
          id: this.observation.id,
          observation: this.observation
        }).then(data => {
          if (data && typeof data == "object") {
            this.onCancelMainDetailsEditing(detailsName);
          }
        });
      }
    },
    /**
     * @function prepareForDisplayRecords
     * @param observation
     * @returns {Array}
     */
    prepareForDisplayRecords(observation) {
      if (observation.recordType === "text" || observation.recordType === "textarea") {
        return this.prepareItemText(observation);
      }

      if (observation.recordType === "dropdown") {
        return this.prepareItemDropdown(observation);
      }

      if (observation.recordType === "radio") {
        return this.prepareItemRadio(observation);
      }

      if (observation.recordType === "number") {
        return this.prepareItemNumber(observation);
      }

      if (observation.recordType === "datetime") {
        return this.prepareItemDateTime(observation);
      }

      if (
        observation.recordType === "image" &&
        observation.fileObject &&
        observation.fileObject.path
      ) {

        // Prevent image records from being shown more than once
        // - image sub-records appear more than once (e.g. under an organism)
        let img = this.images.filter(img => {
          if (img.path === observation.fileObject.path) {
            return img
          }
        })
        if (img && img.length < 1) {
          this.images.push({
            path: observation.fileObject.path,
            current: true
          });
        }

        return this.prepareItemImage(observation);
      }

      return null;
    },

    /**
     * @function onAddModeDataClick - click handler for add more data button
     */
    onAddModeDataClick() {
      this.showAddModeData = !this.showAddModeData;
    },

    /**
     * @function onSubmitAddModeDataClick - click handler for submit button
     */
    onSubmitAddModeDataClick() {
      let type = "text";
      let value = "";
      let unit = "";
      let question_text = "";
      let record = null;

      let lastOrderNumber = 0;
      this.observation.records.forEach((record, recordIndex) => {
        if (lastOrderNumber < record.orderNumber) {
          lastOrderNumber = record.orderNumber;
        }
      });

      if (this.addMoreDataValueType === "") {
        return;
      } else if (this.addMoreDataValueType === "number") {
        if (isNaN(this.addMoreDataValue)) {
          // not a number
          return;
        }

        this.addMoreDataValue = parseFloat(this.addMoreDataValue).toFixed(1);
        record = this.prepareAddNumberRecord(lastOrderNumber);
      } else if (
        this.addMoreDataValueType === "text" ||
        this.addMoreDataValueType === "textarea"
      ) {
        record = this.prepareAddTextRecord(lastOrderNumber);
      } else if (this.addMoreDataValueType === "datetime") {
        record = this.prepareAddDateTimeRecord(lastOrderNumber);
      } else if (this.addMoreDataValueType === "dropdown") {
        record = this.prepareAddDropdownRecord(lastOrderNumber);
      } else if (this.addMoreDataValueType === "radio") {
        record = this.prepareAddDropdownRecord(lastOrderNumber);
      } else if (this.addMoreDataValueType === "image") {
        this.prepareAddImageRecord(lastOrderNumber, value);

        return;
      }

      this.createRecord(record).then(data => {
        this.addMoreDataQ1 = 1;
        this.addMoreDataMN = "";
        this.addMoreDataValueType = "";
        this.addMoreDataValue = "";
        this.addMoreDataUnits = "";
        this.textAreaValue = "";
        this.dateValue = "";
        this.dropDownList = [
          {
            fieldVal: ""
          }
        ];
        this.chooseList = [
          {
            fieldVal: ""
          }
        ];
      });
    },

    /**
     * @function prepareAddTextRecord
     * @param lastOrderNumber
     * @returns {{orderNumber: *, field: {dataType: string}, records: Array, recordType: string, observation: *, label: string, value: string}}
     */
    prepareAddTextRecord(lastOrderNumber) {
      return {
        label: this.addMoreDataMN,
        field: {
          dataType: "text"
        },
        orderNumber: lastOrderNumber + 1,
        recordType: this.addMoreDataValueType,
        records: [],
        value: this.textAreaValue,
        observation: this.observation["@id"]
      };
    },

    /**
     * @function prepareAddNumberRecord
     * @param lastOrderNumber
     * @returns {{orderNumber: *, field: {dataType: string}, records: Array, recordType: string, observation: *, label: string, value: *}}
     */
    prepareAddNumberRecord(lastOrderNumber) {
      return {
        label:
          this.addMoreDataMN +
          (this.addMoreDataUnits.length
            ? " (" + this.addMoreDataUnits + ")"
            : ""),
        field: {
          dataType: "number"
        },
        orderNumber: lastOrderNumber + 1,
        recordType: "number",
        records: [],
        value: this.addMoreDataValue,
        observation: this.observation["@id"]
      };
    },

    /**
     * @function prepareAddDateTimeRecord
     * @param lastOrderNumber
     * @returns {{orderNumber: *, field: {dataType: string}, records: Array, recordType: string, observation: *, label: string, value: Date}}
     */
    prepareAddDateTimeRecord(lastOrderNumber) {
      let dateObj = this.dateForServer(this.dateValue);

      return {
        label: this.addMoreDataMN,
        field: {
          dataType: "datetime"
        },
        orderNumber: lastOrderNumber + 1,
        recordType: "datetime",
        records: [],
        value: dateObj,
        observation: this.observation["@id"]
      };
    },

    /**
     * @function prepareAddImageRecord
     * @param lastOrderNumber
     */
    prepareAddImageRecord(lastOrderNumber) {
      this.uploadFile({fd: this.recordImageUpload}).then(data => {
        this.createRecord({
          label: this.addMoreDataMN,
          field: {
            dataType: "file"
          },
          orderNumber: lastOrderNumber + 1,
          recordType: "image",
          records: [],
          fileObject: data["@id"],
          value: null,
          observation: this.observation["@id"]
        }).then(data => {
          this.addMoreDataQ1 = 1;
          this.addMoreDataMN = "";
          this.addMoreDataValueType = "";
          this.addMoreDataValue = "";
          this.addMoreDataUnits = "";
          this.textAreaValue = "";
          this.dateValue = "";
        });
      });
    },

    /**
     * @function prepareAddDropdownRecord
     * @param lastOrderNumber
     * @returns {*}
     */
    prepareAddDropdownRecord(lastOrderNumber) {
      let options = [];
      let recordType = "";

      if (this.displayDropdown) {
        this.dropDownList.forEach((observation, index) => {
          options.push({
            dataType: "text",
            value: observation.fieldVal,
            orderNumber: index
          });
        });

        recordType = "dropdown";
      } else if (this.displayRadio) {
        this.chooseList.forEach((observation, index) => {
          options.push({
            dataType: "text",
            value: observation.fieldVal,
            orderNumber: index
          });
        });

        recordType = "radio";
      } else {
        return null;
      }

      return {
        label: this.addMoreDataMN,
        field: {
          dataType: "set",
          optionsSet: {
            optionsValues: options
          }
        },
        orderNumber: lastOrderNumber + 1,
        recordType: recordType,
        records: [],
        observation: this.observation["@id"]
      };
    },

    /**
     *
     * @returns {void|*}
     */
    onFileUploadNewRecord(file) {
      const _files = file.target.files || file.dataTransfer.files;
      const fileTypesAllowed = ["jpg", "jpeg", "png"];
      let object = {};

      if (!_files.length) return;

      const filename = _files[0].name;

      if (
        filename.lastIndexOf(".") <= 0 ||
        fileTypesAllowed.indexOf(
          filename
            .split(".")
            .pop()
            .toLowerCase()
        ) === -1
      ) {
        return this.setError(
          "Please select a valid file type: jpg, jpeg, png"
        );
      }

      this.recordImageUpload = new FormData();
      this.recordImageUpload.append("file", _files[0], _files[0].name);
      this.recordImageUpload.append("actionType", FILE_ACTION_TYPES.observation_image.name);

      const fileReader = new FileReader();

      fileReader.onload = e => {
        this.previewNewRecordImage = e.target.result;
      };

      fileReader.readAsDataURL(_files[0]);
    },

    /**
     * @function addNewFile
     * @param evt
     */
    addNewFile(evt) {
      evt.stopImmediatePropagation();
      evt.preventDefault();

      if (this.displayRadio) {
        this.chooseList.push({fieldVal: ""});
      } else if (this.displayDropdown) {
        this.dropDownList.push({fieldVal: ""});
      }
    },

    /**
     * @function removeField
     * @param evt
     * @param index
     */
    removeField(evt, index) {
      evt.stopImmediatePropagation();
      evt.preventDefault();

      if (this.displayRadio) {
        this.chooseList.splice(index, 1);
      } else if (this.displayDropdown) {
        this.dropDownList.splice(index, 1);
      }
    },

    /**
     * @function onFileUploadChange - change handler for the file input type
     * @param {object} file - event object
     */
    onFileUploadChange(file) {
      const _files = file.target.files || file.dataTransfer.files;
      const fileTypesAllowed = ["jpg", "jpeg", "png"];
      let object = {};

      if (!_files.length) return;

      const filename = _files[0].name;

      if (
        filename.lastIndexOf(".") <= 0 ||
        fileTypesAllowed.indexOf(
          filename
            .split(".")
            .pop()
            .toLowerCase()
        ) === -1
      ) {
        return this.setError(
          "Please select a valid file type: jpg, jpeg, png"
        );
      }

      let fd = new FormData();
      fd.append("file", _files[0], _files[0].name);
      fd.append("actionType", FILE_ACTION_TYPES.observation_image.name);
      fd.append("projectId", this.observation.project.id)

      this.uploadFile({fd: fd}).then(data => {
        let observation = {files: []};

        this.observation.files.forEach((obs, index) => {
          observation.files.push(obs["@id"]);
        });

        observation.files.push({
          file: data["@id"]
        });
        // this.observationUpdate({id: this.observation.id, observation: observation});
        this.updateObservation({id: this.observation.id, observation: observation});
      });
    },

    /**
     * @function prepareItemText
     * @param observation
     * @returns {{question_text: *, questionId: *, edit: boolean, answers: {id: *, value: *, selected: boolean}[], type: *}}
     */
    prepareItemText(observation) {
      return {
        questionId: observation.id,
        question_text: observation.label,
        type: observation.recordType,
        edit: false,
        answers: [
          {
            id: observation.id,
            value: observation.value,
            selected: true
          }
        ]
      };
    },

    /**
     * @function prepareItemDropdown
     * @param observation
     * @returns {{question_text: *, questionId: *, edit: boolean, answers: Array, type: *}}
     */
    prepareItemDropdown(observation) {
      return {
        questionId: observation.id,
        question_text: observation.label,
        type: observation.recordType,
        edit: false,
        answers: [
          {
            id: observation.optionValue ? observation.optionValue.id : "",
            value: observation.optionValue ? observation.optionValue.value : observation.value,
            selected: true
          }
        ]
      };
    },

    /**
     * @function prepareItemRadio
     * @param observation
     * @returns {{question_text: *, questionId: *, edit: boolean, answers: Array, type: *}}
     */
    prepareItemRadio(observation) {
      return {
        questionId: observation.id,
        question_text: observation.label,
        type: observation.recordType,
        edit: false,
        answers: [
          {
            id: observation.optionValue.id,
            value: observation.optionValue.value,
            selected: true
          }
        ]
      };
    },

    /**
     * @function prepareItemNumber
     * @param observation
     * @returns {{question_text: *, questionId: *, edit: boolean, answers: {id: *, value: *, selected: boolean}[], type: *}}
     */
    prepareItemNumber(observation) {
      return {
        questionId: observation.id,
        question_text: observation.label,
        type: observation.recordType,
        edit: false,
        answers: [
          {
            id: observation.id,
            value: observation.value,
            selected: false
          }
        ]
      };
    },

    /**
     * @function prepareItemDateTime
     * @param observation
     * @returns {{question_text: *, questionId: *, edit: boolean, answers: {id: *, value: *, selected: boolean}[], type: *}}
     */
    prepareItemDateTime(observation) {
      return {
        questionId: observation.id,
        question_text: observation.label,
        type: observation.recordType,
        edit: false,
        answers: [
          {
            id: observation.id,
            value: observation.value,
            selected: false
          }
        ]
      };
    },

    /**
     * @function prepareItemImage
     * @param observation
     * @returns {{question_text: *, questionId: *, edit: boolean, answers: {id: *, value: Array, selected: boolean}[], type: *}}
     */
    prepareItemImage(observation) {
      return {
        questionId: observation.id,
        question_text: observation.label,
        type: observation.recordType,
        edit: false,
        answers: [
          {
            id: observation.id,
            value:
              observation.fileObject && observation.fileObject.path
                ? observation.fileObject.path
                : "",
            selected: true
          }
        ]
      };
    },

    /**
     * @function onDeleteObservationClick - when the user click on delete observation button
     * @param event
     */
    onDeleteObservationClick(event) {
      event.stopImmediatePropagation();
      event.preventDefault();
      this.resetError();

      this.$refs.confirmationModal.show();
    },

    /**
     * @function clickAddDataLink - function that handle the Add Data button press. Check if user is logged in.
     *    If the user is not logged in display a popup with a message if it is redirecting the user to the datasheet
     */
    clickAddDataLink() {
      let datasheetId = this.observation.datasheet['@id'].replace("/datasheets/", "");

      if (JSON.parse(localStorage.getItem("sessionData")) == null) {
        this.$root.$emit("bv::show::modal", "not-logged-in-modal");
        return;
      } else {
        this.$router.push({
          name: "DatasheetView",
          params: {id: datasheetId}
        });
      }
    },

    /**
     * @function onYesClick - when the user click on Yes button on delete confirmation
     */
    onYesClick() {
      this.deleteObservation({id: this.observation.id}).then(data => {
        if (typeof data === "boolean" && data === false) {
          this.$refs.confirmationModal.hide();
        } else {
          this.$router.push({
            name: "ProjectShow",
            params: {id: this.observation.project.urlField}
          });
        }
      });
    },

    /**
     * @function onNoClick - when the user click on No button on delete confirmation
     */
    onNoClick() {
      this.$refs.confirmationModal.hide();
    },

    /**
     * @function onDismissedClick - when the user dismiss error alert, we reset error message
     */
    onDismissedClick() {
      this.resetError();
    },

    /**
     * @function onCancelImageUpload
     * @param evt
     */
    onCancelImageUpload(evt) {
      if (this.previewNewRecordImage != null) {
        this.previewNewRecordImage = null;
      } else if (this.previewEditRecordImage != null) {
        this.previewEditRecordImage = null;
      }
    },

    /**
     *
     */
    onEditActiveChange() {
      this.editActive = !this.editActive;
    },

    /**
     * @function getOrganismName
     * @param organismRecordSelectedSpecies
     * @returns {string}
     */
    getOrganismName(organismRecordSelectedSpecies) {
      return organismRecordSelectedSpecies
        ? organismRecordSelectedSpecies.customName
          ? organismRecordSelectedSpecies.customName
          : organismRecordSelectedSpecies.canonicalName &&
          organismRecordSelectedSpecies.scientificName
            ? organismRecordSelectedSpecies.canonicalName +
            " (" +
            organismRecordSelectedSpecies.scientificName +
            ")"
            : ""
        : "";
    },

    /**
     * @function locationDetailsShow
     * @param routeName
     * @param routeParam
     */
    locationDetailsShow(routeName, routeParam) {
      this.$router.push({name: routeName, params: {id: routeParam}});
    },

    /**
     * @function getDisplayDateTime - For now, we've decided to not deal with
     *  timezone so this function will remove the timezone portion of the date
     *  (+), if it exists in the value to avoid incorrect timezone conversion
     * @param {string/object} value - the date value to use, can be either
     *  String or Object (will assume to be a Date object)
     * @param {String} the date&time using moment (without timezone)
     */
    getDisplayDateTime(value) {
      if (value) {
        let index = -1;
        let type = typeof value;

        // Tried using .equals() and .compareTo() function but got runtime
        // error so have to use ==
        if (type == "string") index = value.search(/\+/);
        else if (type == "object") value = value.toISOString();

        if (index == -1) {
          return moment(value).format("ddd MMMM DD, YYYY h:mm:ss a");
        } else {
          let new_date = value.substring(0, index);
          return moment(new_date).format("ddd MMMM DD, YYYY h:mm:ss a");
        }
      }
      return "";
    }
  },

  created() {
    this.$root.$on("editActiveChange", this.onEditActiveChange);
    this.resetError();
    this.isModal = !!this.observationId
    const id = this.observationId || this.$route.params.id;
    this.getObservation({id}).then(data => {
      if (data) {
        this.locationData = data.location;
      }
    });
  }
};

</script>

<style scoped lang="scss">
.text-blue {
  color: #00c8f8;
  font-size: 22px;
}

.font-14 {
  font-size: 14px;
  font-weight: bold;
}

.text-red {
  color: #d9534f;
  font-size: 22px;
  margin-right: 4px;
}

.social {
  color: #ffffff;

  .button-social.social-button {
    height: 21px;
    padding: 2px 8px;
    font-size: 12px;

    &.facebook__design__flat {
      font-weight: bold;
    }
  }
}

.submitted {
  height: 36px;
  background: #F8F9FA;
  color: #6C757D;
  margin-left: 20px;
}

.btn-delete-special {
  color: #000000;
  background: rgba(217, 83, 79, 0.3);
  margin-right: -210px;

  &:hover {
    .text-red {
      color: #ffffff;
    }
  }
}

.blue-box {
  border: solid 2px #125EA2;
  background-color: #D9EBF9;
  width: 100%;
}

.width-one {
  width: 150px;
}

.width-two {
  width: 250px;
}

.preview-image {
  width: 100%;
  height: auto;
}

.small-images {
  width: 100px;
  height: auto;

  &.selected {
    border: 1px solid #00c8f8;
  }
}

.btn-blue {
  .btn-font-awesome {
    font-size: 20px;
  }
}

.icon-twitter {
  background-color: #1b95e0;
}

.icon-twitter:hover {
  background-color: #263960;
}

.icon-twitter::before {
}

.icon-facebook {
  background-color: #3b5998;
}

.icon-facebook:hover {
  background-color: #263960;
}

.icon-facebook::before {
}

.social-text {
  margin: 2px;
  font-weight: bold;
}

.paddingRight40px {
  padding-right: 40px;
}

.my-custom-carousel {
  //.img-fluid in above
  //max-width: 100%;
  //max-height: 300px !important;

  //height: 300px;
  width: 100%;
  background-position: center;
  background-attachment: fixed;
  background-position: center;
  text-align: center;
  //background-size: cover;
  //background-color: #00B0F0;
}

.observation-detail-label {
  color: grey;
  font-size: 1.3rem;
  line-height: 1.3;
}

.observation-detail-description {
  width: 100%;
  padding-left: 2px;
  padding-right: 8px;
  color: grey;
  font-size: 1.3rem;
  line-height: 1.3;
}

.label-text {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: normal;
  color: #ffffff;
}

.clearBoth {
  clear: both;
}

.display-content {
  display: contents;
}


</style>
